// EmployerModuleDetailView — the employer course READ page (/dashboard/courses/:id).
//
// Sibling of NeedDetail: a clean read view of a course (via the shared
// ModuleView) with the same action language — Redigera (→ the editor), Fråga
// Link, Förhandsgranska (student overlay), Radera. Row-click in the Moduler list
// lands here; the pencil still opens the editor directly.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, GraduationCap, X, Send, Archive, RotateCcw } from 'lucide-react';
import { ModuleService } from '@/services/ModuleService';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { moduleApi } from '@/lib/api/moduleApi';
import type { ModuleFullResponse, CourseStatus } from '@/lib/models/module';
import ModuleView, { moduleToView } from '@/components/modules/ModuleView';
import ModuleViewer from '@/components/modules/ModuleViewer';
import { useLinkLauncher } from '@/contexts/LinkLauncherContext';
import { ChattaMedLinkButton } from '@/components/custom-ui/ChattaMedLinkButton';
import { EntityActionMenu } from '@/components/custom-ui/EntityActionMenu';
import {
    LoadingState, ErrorState, DangerButton,
    DeleteConfirmDialog, IslandSlideOver,
    ConfirmDialog, StatusBadge,
} from '@/components/custom-ui';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';

const EmployerModuleDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { stageLink } = useLinkLauncher();

    const [module, setModule] = useState<ModuleFullResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    // #75 course lifecycle transition.
    const [pendingStatus, setPendingStatus] = useState<{
        status: CourseStatus; title: string; description: string; confirmLabel: string;
    } | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);

    const confirmStatusChange = async () => {
        if (!module || !pendingStatus) return;
        setStatusBusy(true);
        try {
            await moduleApi.updateModuleStatus(module.id, pendingStatus.status);
            setPendingStatus(null);
            await loadModule();
        } catch (err) {
            console.error('Failed to change course status:', err);
            alert('Kunde inte ändra modulens status.');
        } finally {
            setStatusBusy(false);
        }
    };

    // The lifecycle action offered given the course's current status.
    const lifecycleAction = (status: CourseStatus) => {
        if (status === 'draft') {
            return {
                label: 'Publicera', icon: Send,
                onClick: () => setPendingStatus({
                    status: 'published',
                    title: 'Publicera modulen?',
                    description: 'Modulen blir tillgänglig att lägga till i program och som förkunskapskrav vid rekrytering. Först då kan kandidater se den.',
                    confirmLabel: 'Publicera',
                }),
            };
        }
        if (status === 'published') {
            return {
                label: 'Arkivera', icon: Archive,
                onClick: () => setPendingStatus({
                    status: 'archived',
                    title: 'Arkivera modulen?',
                    description: 'Modulen döljs för nya kandidater och kan inte längre läggas till i program eller annonser. Kandidater som redan påbörjat den påverkas inte. Du kan återpublicera senare.',
                    confirmLabel: 'Arkivera',
                }),
            };
        }
        return {
            label: 'Återpublicera', icon: RotateCcw,
            onClick: () => setPendingStatus({
                status: 'published',
                title: 'Återpublicera modulen?',
                description: 'Modulen blir tillgänglig igen och kan läggas till i program och annonser.',
                confirmLabel: 'Återpublicera',
            }),
        };
    };

    useEffect(() => { if (id) loadModule(); }, [id]);

    const loadModule = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const result = await ModuleService.getModuleFull(id);
            if (!result) { setError('Modulen hittades inte'); return; }
            setModule(result);
        } catch (err) {
            console.error('Failed to load module:', err);
            setError('Kunde inte ladda modulen');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => navigate('/dashboard/courses');

    const handleDelete = async () => {
        if (!module) return;
        setIsDeleting(true);
        try {
            await onboardingApi.deleteOnboardingModule(module.id);
            navigate('/dashboard/courses');
        } catch (err) {
            console.error('Failed to delete module:', err);
            alert('Kunde inte radera modulen.');
            setIsDeleting(false);
        }
    };

    if (loading) return <LoadingState message="Laddar modul..." />;
    if (error || !module) return <ErrorState title="Kunde inte ladda modulen" message={error || 'Modulen hittades inte'} onRetry={handleBack} />;

    const status: CourseStatus = (module.status as CourseStatus) ?? 'draft';
    const action = lifecycleAction(status);

    return (
        <div className={NEXILINK_SPACING}>
            <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowLeft className="w-4 h-4" /> <span className="text-sm font-medium">Tillbaka till moduler</span>
            </button>

            {/* #75 — a draft course is invisible to candidates until published.
                Make that state obvious with a one-click publish. */}
            {status === 'draft' && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <div className="flex items-start gap-2 text-sm text-amber-800">
                        <Send className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Den här modulen är ett <strong>utkast</strong> och syns inte för kandidater. Publicera den för att kunna lägga till den i program och annonser.</span>
                    </div>
                    <button
                        onClick={action.onClick}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#1A4D4F] text-white hover:bg-[#164042] transition-colors flex-shrink-0"
                    >
                        <Send className="w-4 h-4" /> Publicera
                    </button>
                </div>
            )}

            <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-4 sm:p-6`}>
                <ModuleView
                    data={moduleToView(module)}
                    actions={
                        <>
                            <StatusBadge status={status} />
                            {/* B6 — round Link entry point + the ordinary actions folded into a kebab. */}
                            <ChattaMedLinkButton
                                onClick={() => {
                                    // #301 — staged (unsent) @-tagged prompt referencing this modul.
                                    stageLink({
                                        reference: { type: 'module', id: module.id },
                                        label: module.module_name,
                                        prompt: 'Hjälp mig att jobba vidare med den här modulen.',
                                    });
                                }}
                            />
                            <EntityActionMenu
                                actions={[
                                    { label: 'Redigera', icon: Edit2, onClick: () => navigate(`/dashboard/onboarding/modules/${module.id}`) },
                                    { label: 'Förhandsgranska', icon: GraduationCap, onClick: () => setPreviewOpen(true) },
                                    // #75 lifecycle transition (Publicera / Arkivera / Återpublicera).
                                    { label: action.label, icon: action.icon, onClick: action.onClick },
                                ]}
                            />
                        </>
                    }
                >
                    {/* Danger zone */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                        <DangerButton icon={Trash2} onClick={() => setDeleteOpen(true)}>Radera modul</DangerButton>
                    </div>
                </ModuleView>
            </div>

            {/* Student preview overlay (parallels the list eye + editor "Visa som student") */}
            {previewOpen && (
                <IslandSlideOver onClose={() => setPreviewOpen(false)} fullScreen zClass="z-50">
                    <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <GraduationCap className="w-4 h-4 text-[#1A4D4F]" /> Studentvy — {module.module_name}
                        </div>
                        <button onClick={() => setPreviewOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Stäng">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-50/40">
                        <div className="max-w-3xl mx-auto">
                            <ModuleViewer moduleId={module.id} preview onClose={() => setPreviewOpen(false)} />
                        </div>
                    </div>
                </IslandSlideOver>
            )}

            <DeleteConfirmDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                title="Radera modul"
                entityLabel="modulen"
                entityName={module.module_name}
                onConfirm={handleDelete}
                isDeleting={isDeleting}
            />

            <ConfirmDialog
                open={pendingStatus !== null}
                onOpenChange={(open) => { if (!open) setPendingStatus(null); }}
                title={pendingStatus?.title || ''}
                description={pendingStatus?.description}
                confirmLabel={pendingStatus?.confirmLabel}
                onConfirm={confirmStatusChange}
                isBusy={statusBusy}
            />
        </div>
    );
};

export default EmployerModuleDetailView;
